import React from 'react';
import { Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    booleanCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    indexCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { ApiRoutes } from '../Common';
import { PageHeading, PageWrap } from '../common/forms/FormElements';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import CallDispositionForm from './CallDispositionForm';

class CallDispositionIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
        };
        this.formRef = React.createRef();
    }

    componentDidMount() {
        return this.populateState();
    }

    onAddDisposition = () => {
        this.formRef.current.openNew();
    };

    onEditDisposition = (id) => {
        this.formRef.current.open(id);
    };

    onFormSaved = () => {
        this.state.gridOptions.refresh();
    };

    async populateState() {
        const [directions] = await Promise.all([
            httpClient.js(ApiRoutes.callDisposition.directions()),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
        };

        const activeFilterParams = {
            suppressFilterButton: true,
            options: [
                { name: 'Yes', id: 'true' },
                { name: 'No', id: 'false' },
            ],
            optionsLabel: 'name',
            optionsValue: 'id',
        };

        const directionFilterParams = {
            suppressFilterButton: true,
            options: directions,
            optionsLabel: 'description',
            optionsValue: 'id',
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                maxWidth: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Description',
                sortable: true,
                headerName: 'Description',
                field: 'description',
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: (id) => this.onEditDisposition(id),
                    nameField: 'description',
                    idField: 'id',
                    title: 'Edit this Disposition',
                },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Directions',
                headerName: 'Directions',
                sortable: false,
                field: 'directions',
                valueFormatter: ({ value }) => (value || []).map((d) => d.description).join(', '),
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: directionFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: directionFilterParams,
            },
            {
                colId: 'NotesRequired',
                sortable: true,
                headerName: 'Notes Required',
                field: 'notesRequired',
                cellRenderer: booleanCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: activeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: activeFilterParams,
            },
            {
                colId: 'IsActive',
                sortable: true,
                headerName: 'Active',
                field: 'isActive',
                cellRenderer: booleanCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: activeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: activeFilterParams,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.callDisposition.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            directions,
        });
    }

    render() {
        if (this.state.loading) return <Progress />;

        const { rowData, gridOptions, directions } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faPhoneVolume}
                        className="mr-2 text-muted"
                    />
                    <span>Call Dispositions</span>
                </PageHeading>
                <DataGridToolbar
                    entity="callDisposition"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={this.onAddDisposition}
                    addLabel="Add Disposition"
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
                <CallDispositionForm
                    ref={this.formRef}
                    directions={directions}
                    onSaved={this.onFormSaved}
                />
            </PageWrap>
        );
    }
}

export default CallDispositionIndex;
